"use client";

import { useEffect, useRef, useCallback, useTransition } from "react";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { SiriOrb } from "@/components/ui/siri-orb";
import {
    MailIcon,
    SearchIcon,
    TargetIcon,
    TrendingUpIcon,
    Paperclip,
    SendIcon,
    XIcon,
    LoaderIcon,
    Command,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import * as React from "react";

interface UseAutoResizeTextareaProps {
    minHeight: number;
    maxHeight?: number;
    /** When false, skip pixel height — lets flex layout fill the parent (e.g. Flow widget). */
    enabled?: boolean;
}

function useAutoResizeTextarea({ minHeight, maxHeight, enabled = true }: UseAutoResizeTextareaProps) {
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const adjustHeight = useCallback(
        (reset?: boolean) => {
            if (!enabled) return;
            const textarea = textareaRef.current;
            if (!textarea) return;
            if (reset) {
                textarea.style.height = `${minHeight}px`;
                return;
            }
            textarea.style.height = `${minHeight}px`;
            const newHeight = Math.max(
                minHeight,
                Math.min(textarea.scrollHeight, maxHeight ?? Number.POSITIVE_INFINITY)
            );
            textarea.style.height = `${newHeight}px`;
        },
        [minHeight, maxHeight, enabled]
    );

    useEffect(() => {
        const textarea = textareaRef.current;
        if (!textarea) return;
        if (!enabled) {
            textarea.style.height = "";
            return;
        }
        textarea.style.height = `${minHeight}px`;
    }, [minHeight, enabled]);

    useEffect(() => {
        if (!enabled) return;
        const handleResize = () => adjustHeight();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [adjustHeight, enabled]);

    return { textareaRef, adjustHeight };
}

interface CommandSuggestion {
    icon: React.ReactNode;
    label: string;
    description: string;
    prefix: string;
}

interface PromptSuggestion {
    icon: React.ReactNode;
    label: string;
    text: string;
}

export interface AnimatedAIChatProps {
    onSubmit?: (value: string) => void;
    isLoading?: boolean;
    typingBadge?: "ai" | "orb";
    minimal?: boolean;
    className?: string;
}

export function AnimatedAIChat({
    onSubmit,
    isLoading: externalLoading,
    typingBadge = "ai",
    minimal = false,
    className,
}: AnimatedAIChatProps) {
    const [value, setValue] = useState("");
    const [attachments, setAttachments] = useState<string[]>([]);
    const [internalTyping, setInternalTyping] = useState(false);
    const [, startTransition] = useTransition();
    const [activeSuggestion, setActiveSuggestion] = useState<number>(-1);
    const [showCommandPalette, setShowCommandPalette] = useState(false);
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
    const { textareaRef, adjustHeight } = useAutoResizeTextarea({
        minHeight: 60,
        maxHeight: 200,
        enabled: !minimal,
    });
    const [inputFocused, setInputFocused] = useState(false);
    const commandPaletteRef = useRef<HTMLDivElement>(null);

    const isTyping = externalLoading ?? internalTyping;

    const commandSuggestions: CommandSuggestion[] = [
        { icon: <MailIcon className="w-4 h-4" />, label: "Outreach", description: "Write investor outreach emails", prefix: "/outreach" },
        { icon: <SearchIcon className="w-4 h-4" />, label: "Research", description: "Find investors by stage or sector", prefix: "/research" },
        { icon: <TargetIcon className="w-4 h-4" />, label: "Investors", description: "Build your target investor list", prefix: "/investors" },
        { icon: <TrendingUpIcon className="w-4 h-4" />, label: "Strategy", description: "Get fundraising strategy advice", prefix: "/strategy" },
    ];

    const promptSuggestions: PromptSuggestion[] = [
        { icon: <MailIcon className="w-4 h-4" />, label: "Write outreach", text: "Write a concise outreach email to a seed-stage fintech investor." },
        { icon: <SearchIcon className="w-4 h-4" />, label: "Find investors", text: "Find seed-stage fintech investors based in New York." },
        { icon: <TargetIcon className="w-4 h-4" />, label: "Build list", text: "Help me build a target investor list for my pre-seed round." },
        { icon: <TrendingUpIcon className="w-4 h-4" />, label: "Fundraise strategy", text: "What's the best fundraising strategy for my Series A?" },
    ];

    useEffect(() => {
        if (!minimal && value.startsWith("/") && !value.includes(" ")) {
            setShowCommandPalette(true);
            const idx = commandSuggestions.findIndex((cmd) => cmd.prefix.startsWith(value));
            setActiveSuggestion(idx >= 0 ? idx : -1);
        } else {
            setShowCommandPalette(false);
        }
    // commandSuggestions is stable (defined inline but identical each render)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, minimal]);

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => setMousePosition({ x: e.clientX, y: e.clientY });
        window.addEventListener("mousemove", handleMouseMove);
        return () => window.removeEventListener("mousemove", handleMouseMove);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            const commandButton = document.querySelector("[data-command-button]");
            if (
                commandPaletteRef.current &&
                !commandPaletteRef.current.contains(target) &&
                !commandButton?.contains(target)
            ) {
                setShowCommandPalette(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (showCommandPalette) {
            if (e.key === "ArrowDown") {
                e.preventDefault();
                setActiveSuggestion((prev) => (prev < commandSuggestions.length - 1 ? prev + 1 : 0));
            } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setActiveSuggestion((prev) => (prev > 0 ? prev - 1 : commandSuggestions.length - 1));
            } else if (e.key === "Tab" || e.key === "Enter") {
                e.preventDefault();
                if (activeSuggestion >= 0) {
                    setValue(commandSuggestions[activeSuggestion].prefix + " ");
                    setShowCommandPalette(false);
                }
            } else if (e.key === "Escape") {
                e.preventDefault();
                setShowCommandPalette(false);
            }
        } else if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            if (value.trim()) handleSendMessage();
        }
    };

    const handleSendMessage = () => {
        if (!value.trim()) return;
        if (onSubmit) {
            onSubmit(value.trim());
            setValue("");
            if (!minimal) adjustHeight(true);
        } else {
            startTransition(() => {
                setInternalTyping(true);
                setTimeout(() => {
                    setInternalTyping(false);
                    setValue("");
                    if (!minimal) adjustHeight(true);
                }, 3000);
            });
        }
    };

    const handleAttachFile = () => {
        if (minimal) return;
        const mockFileName = `file-${Math.floor(Math.random() * 1000)}.pdf`;
        setAttachments((prev) => [...prev, mockFileName]);
    };

    const removeAttachment = (index: number) => {
        setAttachments((prev) => prev.filter((_, i) => i !== index));
    };

    const selectCommandSuggestion = (index: number) => {
        setValue(commandSuggestions[index].prefix + " ");
        setShowCommandPalette(false);
    };

    const selectPromptSuggestion = (text: string) => {
        setValue(text);
        if (!minimal) adjustHeight();
    };

    return (
        <div
            className={cn(
                "relative w-full space-y-3",
                minimal && "flex h-full min-h-0 flex-1 flex-col space-y-2",
                className
            )}
        >
            {/* Animated input box */}
            <motion.div
                className={cn(
                    "relative rounded-2xl border border-slate-200/90 bg-white shadow-[0_2px_16px_rgba(15,23,42,0.07),0_0_0_1px_rgba(15,23,42,0.02)] transition-shadow focus-within:shadow-[0_4px_24px_rgba(15,23,42,0.1),0_0_0_1px_rgba(43,127,255,0.12)] dark:border-white/[0.07] dark:bg-[rgba(10,16,36,0.8)] dark:shadow-[0_2px_20px_rgba(0,0,0,0.4)] dark:backdrop-blur-2xl dark:focus-within:border-[#2B7FFF]/25 dark:focus-within:shadow-[0_4px_28px_rgba(0,0,0,0.5)]",
                    minimal && "flex min-h-0 flex-1 flex-col"
                )}
                initial={minimal ? false : { scale: 0.98, opacity: 0 }}
                animate={minimal ? undefined : { scale: 1, opacity: 1 }}
                transition={minimal ? undefined : { duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            >
                {/* Command palette */}
                <AnimatePresence>
                    {!minimal && showCommandPalette && (
                        <motion.div
                            ref={commandPaletteRef}
                            className="absolute left-4 right-4 bottom-full mb-2 backdrop-blur-xl bg-white dark:bg-black/90 rounded-xl z-50 shadow-lg border border-slate-200 dark:border-white/10 overflow-hidden"
                            initial={{ opacity: 0, y: 5 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 5 }}
                            transition={{ duration: 0.15 }}
                        >
                            <div className="py-1">
                                {commandSuggestions.map((suggestion, index) => (
                                    <motion.div
                                        key={suggestion.prefix}
                                        className={cn(
                                            "flex items-center gap-2 px-3 py-2.5 text-xs transition-colors cursor-pointer",
                                            activeSuggestion === index
                                                ? "bg-[#2B7FFF]/[0.08] text-[#2B7FFF] dark:bg-white/10 dark:text-white"
                                                : "text-slate-600 dark:text-white/70 hover:bg-slate-50 dark:hover:bg-white/5"
                                        )}
                                        onClick={() => selectCommandSuggestion(index)}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        transition={{ delay: index * 0.03 }}
                                    >
                                        <div
                                            className={cn(
                                                "w-5 h-5 flex items-center justify-center",
                                                activeSuggestion === index
                                                    ? "text-[#2B7FFF] dark:text-white/90"
                                                    : "text-slate-400 dark:text-white/60"
                                            )}
                                        >
                                            {suggestion.icon}
                                        </div>
                                        <span className="font-medium">{suggestion.label}</span>
                                        <span className="text-slate-300 dark:text-white/30 ml-1">{suggestion.prefix}</span>
                                        <span className="ml-auto text-slate-400 dark:text-white/30">{suggestion.description}</span>
                                    </motion.div>
                                ))}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Textarea */}
                <div
                    className={cn(
                        "px-5 pt-4 pb-2",
                        minimal && "flex min-h-0 flex-1 flex-col"
                    )}
                >
                    <textarea
                        ref={textareaRef}
                        value={value}
                        onChange={(e) => {
                            setValue(e.target.value);
                            if (!minimal) adjustHeight();
                        }}
                        onKeyDown={handleKeyDown}
                        onFocus={() => setInputFocused(true)}
                        onBlur={() => setInputFocused(false)}
                        placeholder="Ask about investors, outreach, fundraising strategy..."
                        className={cn(
                            "w-full resize-none bg-transparent text-[13.5px] leading-relaxed text-slate-900 placeholder:text-slate-400 focus:outline-none dark:text-white/90 dark:placeholder:text-white/20 min-h-[60px]",
                            minimal &&
                                "min-h-[7rem] flex-1 overflow-y-auto text-slate-100 placeholder:text-slate-500 dark:text-white/90 dark:placeholder:text-white/35"
                        )}
                        style={minimal ? undefined : { overflow: "hidden" }}
                    />
                </div>

                {/* Attachments */}
                <AnimatePresence>
                    {!minimal && attachments.length > 0 && (
                        <motion.div
                            className="px-5 pb-2 flex gap-2 flex-wrap"
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                        >
                            {attachments.map((file, index) => (
                                <motion.div
                                    key={index}
                                    className="flex items-center gap-2 text-xs bg-slate-100 dark:bg-white/[0.04] py-1.5 px-3 rounded-lg text-slate-600 dark:text-white/60 border border-slate-200/60 dark:border-white/[0.06]"
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.9 }}
                                >
                                    <span>{file}</span>
                                    <button
                                        onClick={() => removeAttachment(index)}
                                        className="text-slate-400 dark:text-white/30 hover:text-slate-600 dark:hover:text-white transition-colors"
                                    >
                                        <XIcon className="w-3 h-3" />
                                    </button>
                                </motion.div>
                            ))}
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Toolbar */}
                <div
                    className={cn(
                        "flex shrink-0 items-center border-t border-slate-100 px-4 pb-3.5 pt-1 dark:border-white/[0.06]",
                        minimal ? "justify-end border-white/10 bg-white/[0.02]" : "justify-between"
                    )}
                >
                    {!minimal && (
                        <div className="flex items-center gap-1">
                            <motion.button
                                type="button"
                                onClick={handleAttachFile}
                                whileTap={{ scale: 0.94 }}
                                className="p-2 text-slate-400 dark:text-white/30 hover:text-slate-600 dark:hover:text-white/80 rounded-lg transition-colors hover:bg-slate-100 dark:hover:bg-white/[0.06]"
                                title="Attach file"
                            >
                                <Paperclip className="w-4 h-4" />
                            </motion.button>
                            <motion.button
                                type="button"
                                data-command-button
                                onClick={(e) => { e.stopPropagation(); setShowCommandPalette((prev) => !prev); }}
                                whileTap={{ scale: 0.94 }}
                                className={cn(
                                    "p-2 rounded-lg transition-colors",
                                    showCommandPalette
                                        ? "bg-[#2B7FFF]/10 dark:bg-white/10 text-[#2B7FFF] dark:text-white/80"
                                        : "text-slate-400 dark:text-white/30 hover:text-slate-600 dark:hover:text-white/80 hover:bg-slate-100 dark:hover:bg-white/[0.06]"
                                )}
                                title="Commands"
                            >
                                <Command className="w-4 h-4" />
                            </motion.button>
                            <span className="text-[11px] font-medium text-slate-400/70 dark:text-white/18 ml-2">
                                ⏎ send &nbsp;·&nbsp; ⇧⏎ new line
                            </span>
                        </div>
                    )}

                    <motion.button
                        type="button"
                        onClick={handleSendMessage}
                        whileHover={value.trim() ? { scale: 1.02 } : {}}
                        whileTap={value.trim() ? { scale: 0.97 } : {}}
                        disabled={isTyping || !value.trim()}
                        className={cn(
                            "inline-flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-lg px-4 text-[12px] font-semibold leading-none transition-all [&_svg]:shrink-0",
                            value.trim()
                                ? "bg-[#2B7FFF] text-white shadow-[0_1px_8px_rgba(43,127,255,0.45)] hover:bg-[#1A6AE8] hover:shadow-[0_2px_14px_rgba(43,127,255,0.55)] active:scale-95"
                                : "bg-slate-100 dark:bg-white/[0.04] text-slate-300 dark:text-white/25 cursor-not-allowed"
                        )}
                    >
                        {isTyping ? (
                            <LoaderIcon className="size-3.5 animate-[spin_2s_linear_infinite]" aria-hidden />
                        ) : (
                            <SendIcon className="size-3.5" aria-hidden />
                        )}
                        <span className="leading-none">{isTyping ? "Thinking…" : "Send"}</span>
                    </motion.button>
                </div>
            </motion.div>

            {/* Prompt suggestion chips */}
            {!minimal && (
            <div className="flex flex-wrap items-center justify-center gap-2 pt-1">
                {promptSuggestions.map((suggestion, index) => (
                    <motion.button
                        key={suggestion.label}
                        onClick={() => selectPromptSuggestion(suggestion.text)}
                        className="group flex items-center gap-2 px-3 py-2 bg-white/50 dark:bg-white/[0.03] hover:bg-white dark:hover:bg-white/[0.055] rounded-xl text-[12.5px] text-slate-500 dark:text-white/40 hover:text-slate-800 dark:hover:text-white/65 transition-all border border-slate-200/60 dark:border-white/[0.06] hover:border-slate-300/70 dark:hover:border-white/[0.1] backdrop-blur-sm"
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.26, delay: 0.12 + index * 0.05, ease: "easeOut" }}
                    >
                        <span className="text-[#2B7FFF]/60 dark:text-[#2B7FFF]/55 group-hover:text-[#2B7FFF]/80 dark:group-hover:text-[#2B7FFF]/70 transition-colors">
                            {suggestion.icon}
                        </span>
                        <span>{suggestion.label}</span>
                    </motion.button>
                ))}
            </div>
            )}

            {/* Typing indicator */}
            <AnimatePresence>
                {isTyping && (
                    <motion.div
                        className={cn("flex justify-center pt-1", minimal && "shrink-0")}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.2 }}
                    >
                        <div className="inline-flex items-center gap-3 bg-white/80 dark:bg-white/[0.03] backdrop-blur-xl rounded-full px-4 py-2 shadow border border-slate-200/60 dark:border-white/[0.06]">
                            {typingBadge === "orb" ? (
                                <motion.div
                                    className="flex h-6 w-7 items-center justify-center pointer-events-none"
                                    animate={{ y: [0, -2, 0] }}
                                    transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
                                    aria-hidden
                                >
                                    <SiriOrb size="18px" className="pointer-events-none" animationDuration={14} />
                                </motion.div>
                            ) : (
                                <div className="w-7 h-6 rounded-full bg-[#2B7FFF]/10 dark:bg-white/[0.05] flex items-center justify-center">
                                    <span className="text-[10px] font-semibold text-[#2B7FFF] dark:text-white/90">AI</span>
                                </div>
                            )}
                            <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-white/70">
                                <span>Thinking</span>
                                <TypingDots />
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Cursor-following glow when input is focused */}
            {inputFocused && (
                <motion.div
                    className="fixed w-[50rem] h-[50rem] rounded-full pointer-events-none z-0 opacity-[0.015] bg-gradient-to-r from-[#2B7FFF] via-violet-500 to-indigo-500 blur-[96px]"
                    animate={{ x: mousePosition.x - 400, y: mousePosition.y - 400 }}
                    transition={{ type: "spring", damping: 25, stiffness: 150, mass: 0.5 }}
                />
            )}
        </div>
    );
}

function TypingDots() {
    return (
        <div className="flex items-center ml-1">
            {[1, 2, 3].map((dot) => (
                <motion.div
                    key={dot}
                    className="w-1.5 h-1.5 bg-[#2B7FFF] dark:bg-white/80 rounded-full mx-0.5"
                    initial={{ opacity: 0.3 }}
                    animate={{ opacity: [0.3, 0.9, 0.3], scale: [0.85, 1.1, 0.85] }}
                    transition={{ duration: 1.2, repeat: Infinity, delay: dot * 0.15, ease: "easeInOut" }}
                    style={{ boxShadow: "0 0 4px rgba(43,127,255,0.3)" }}
                />
            ))}
        </div>
    );
}
